import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { ArrowLeft, Save } from "lucide-react";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomersAddPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    customer_id: "",
    company: "",
    building: "",
    address1: "",
    address2: "",
    city: "",
    post_code: "",
    phone_area: "",
    phone1: "",
    phone2: "",
    world_acc: "",
    contact_person: "",
    rate_code: "",
    npwp: "",
    sales_ocs: "",
    sales_area: "",
    date_approval: "",
    email_address: "",
    curr: "IDR",
    city_f: "",
    tax_name: "",
    tax_address: "",
    bill_name: "",
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_id || !formData.company) {
      toast.error('CustomerID dan Company wajib diisi');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/customers`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      toast.success('Customer berhasil ditambahkan!');
      navigate('/customers/list');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menambahkan customer');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="add-customer-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Button
            onClick={() => navigate('/customers/list')}
            variant="ghost"
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to List
          </Button>
          <h1 className="text-4xl font-heading font-bold mb-2">Add New Customer</h1>
          <p className="text-muted-foreground text-lg">Tambah customer baru secara manual</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <Card className="border-none shadow-[0_8px_40px_rgb(0,0,0,0.08)] rounded-2xl">
          <CardHeader>
            <CardTitle className="text-2xl font-heading">Customer Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="customer_id">Customer ID *</Label>
                <Input
                  id="customer_id"
                  name="customer_id"
                  value={formData.customer_id}
                  onChange={handleChange}
                  required
                  placeholder="TEST01"
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company">Company *</Label>
                <Input
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  required
                  placeholder="PT Example Indonesia"
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="building">Building</Label>
                <Input
                  id="building"
                  name="building"
                  value={formData.building}
                  onChange={handleChange}
                  placeholder="Gedung A"
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_person">Contact Person</Label>
                <Input
                  id="contact_person"
                  name="contact_person"
                  value={formData.contact_person}
                  onChange={handleChange}
                  placeholder="John Doe"
                  className="h-11"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="address1">Address 1</Label>
                <Input
                  id="address1"
                  name="address1"
                  value={formData.address1}
                  onChange={handleChange}
                  placeholder="Jl. Sudirman No. 123"
                  className="h-11"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="address2">Address 2</Label>
                <Input
                  id="address2"
                  name="address2"
                  value={formData.address2}
                  onChange={handleChange}
                  placeholder="Lantai 5"
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="Jakarta"
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="post_code">Post Code</Label>
                <Input
                  id="post_code"
                  name="post_code"
                  value={formData.post_code}
                  onChange={handleChange}
                  placeholder="12190"
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone_area">Phone Area</Label>
                <Input
                  id="phone_area"
                  name="phone_area"
                  value={formData.phone_area}
                  onChange={handleChange}
                  placeholder="021"
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone1">Phone 1</Label>
                <Input
                  id="phone1"
                  name="phone1"
                  value={formData.phone1}
                  onChange={handleChange}
                  placeholder="55551234"
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone2">Phone 2</Label>
                <Input
                  id="phone2"
                  name="phone2"
                  value={formData.phone2}
                  onChange={handleChange}
                  placeholder="55551235"
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email_address">Email</Label>
                <Input
                  id="email_address"
                  name="email_address"
                  type="email"
                  value={formData.email_address}
                  onChange={handleChange}
                  placeholder="contact@company.com"
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="world_acc">World Account</Label>
                <Input
                  id="world_acc"
                  name="world_acc"
                  value={formData.world_acc}
                  onChange={handleChange}
                  placeholder="WID-A0001-001"
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rate_code">Rate Code</Label>
                <Input
                  id="rate_code"
                  name="rate_code"
                  value={formData.rate_code}
                  onChange={handleChange}
                  placeholder="PUB_25"
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="npwp">NPWP</Label>
                <Input
                  id="npwp"
                  name="npwp"
                  value={formData.npwp}
                  onChange={handleChange}
                  placeholder="01.234.567.8-901.000"
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sales_ocs">Sales OCS</Label>
                <Input
                  id="sales_ocs"
                  name="sales_ocs"
                  value={formData.sales_ocs}
                  onChange={handleChange}
                  placeholder="SURYA"
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sales_area">Sales Area</Label>
                <Input
                  id="sales_area"
                  name="sales_area"
                  value={formData.sales_area}
                  onChange={handleChange}
                  placeholder="Jakarta"
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="date_approval">Date Approval</Label>
                <Input
                  id="date_approval"
                  name="date_approval"
                  value={formData.date_approval}
                  onChange={handleChange}
                  placeholder="2024-01-15"
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="curr">Currency</Label>
                <Input
                  id="curr"
                  name="curr"
                  value={formData.curr}
                  onChange={handleChange}
                  placeholder="IDR"
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city_f">City F</Label>
                <Input
                  id="city_f"
                  name="city_f"
                  value={formData.city_f}
                  onChange={handleChange}
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tax_name">Tax Name</Label>
                <Input
                  id="tax_name"
                  name="tax_name"
                  value={formData.tax_name}
                  onChange={handleChange}
                  placeholder="PT Example Indonesia"
                  className="h-11"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="tax_address">Tax Address</Label>
                <Input
                  id="tax_address"
                  name="tax_address"
                  value={formData.tax_address}
                  onChange={handleChange}
                  placeholder="Jl. Sudirman No. 123 Jakarta"
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bill_name">Bill Name</Label>
                <Input
                  id="bill_name"
                  name="bill_name"
                  value={formData.bill_name}
                  onChange={handleChange}
                  placeholder="PT Example Indonesia"
                  className="h-11"
                />
              </div>
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 h-12 rounded-full font-bold shadow-hard-pop hover:shadow-hard-pop-hover hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
                data-testid="save-customer-button"
              >
                <Save className="w-5 h-5 mr-2" />
                {loading ? 'Saving...' : 'Save Customer'}
              </Button>
              <Button
                type="button"
                onClick={() => navigate('/customers/list')}
                variant="outline"
                className="rounded-full font-bold border-2"
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
};

export default CustomersAddPage;